import { CloseOutlined } from "@ant-design/icons"
import { Button, Divider, Drawer, Dropdown, Steps, Tag, Tooltip } from "antd"
import {
  ArrowUpRight,
  CheckCheck,
  CheckCircle,
  Copy,
  CopyCheck,
  Mail,
  Redo2,
  SendHorizonal,
  Undo2
} from "lucide-react"
import {
  Dispatch,
  memo,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { Trans, useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router"

import { getPersonDetails } from "~/api/priorities"
import ButtonPrimary from "~/components/base-button"
import Loader, { OSLoader } from "~/components/base-loader"
import CustomTooltip from "~/components/custom-tooltip"
import LinkedinIcon from "~/components/icons/linkedin-icon"
import MarkdownRender from "~/components/markdown-render"
import URLS from "~/constants/urls"
import { useGetAccounts } from "~/hooks/accounts"
import {
  useCopyToClipboard,
  useCopyToClipboardFormatted
} from "~/hooks/useClipboard"
import { useCheckEmailConnection } from "~/hooks/user"
import useAccountsStore from "~/store/accounts"
import useChampionsTrackingStore from "~/store/champions-tracking"
import useGlobalStore from "~/store/global"
import { IAccount } from "~/types/accounts"
import { track } from "~/utils/amplitude"
import { EVENTS, getBaseEventProps } from "~/utils/amplitude-events"
import { retryFetch } from "~/utils/axios"
import { clipText, sanitizeUrl } from "~/utils/helpers"
import { getLocationDisplay } from "~/utils/location-helpers"
import { sleep } from "~/utils/sleep"

export default function EmailTemplate() {
  const { t } = useTranslation()
  const {
    refetch: refetchCheckEmailConnection,
    isRefetching: isRefetchingEmailConnection
  } = useCheckEmailConnection()
  const setConnectEmailProviderModalOpen = useGlobalStore(
    (store) => store.setConnectEmailProviderModalOpen
  )
  const setEmailMessageToSend = useGlobalStore(
    (store) => store.setEmailMessageToSend
  )
  const setEmailRecipient = useGlobalStore((store) => store.setEmailRecipient)
  const [linkedinCopiedText, linkedinCopy] = useCopyToClipboard()
  const [copiedText, copy] = useCopyToClipboardFormatted()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const { id: paramAccountId, priority_id: activeOpportunityId } = useParams()
  // /accounts/people-os/person/:id reuses the :id param for the PERSON
  // (champion uuid), never an account — there, the account id must come from
  // the modal payload instead of the route.
  const { pathname } = useLocation()
  const routeAccountId = pathname.includes("/people-os")
    ? undefined
    : paramAccountId
  const pollCancelledRef = useRef(false)
  const emailTemplateRef = useRef<HTMLDivElement>(null)
  const prevStreamResponse = useRef<any>(null)
  const reader = useRef<ReadableStreamDefaultReader | null>(null)
  const [generatedEmailTemplates, setGeneratedEmailTemplates] = useState<any[]>(
    []
  )
  const [emailTemplateResponse, setEmailTemplateResponse] = useState<
    any | null
  >(null)
  const emailTemplateModal = useAccountsStore(
    (store) => store.emailTemplateModal
  )
  const activeAccountId =
    routeAccountId || emailTemplateModal?.account_id?.toString()
  const setEmailTemplateModal = useAccountsStore(
    (store) => store.setEmailTemplateModal
  )

  const onClose = useCallback(() => {
    pollCancelledRef.current = true
    setEmailTemplateModal(null)
    setGeneratedEmailTemplates([])
    setEmailTemplateResponse(null)
    prevStreamResponse.current = null
    reader.current?.cancel()
    reader.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickNewEmail = useCallback(async () => {
    track(EVENTS.EMAIL.SEND_EMAIL_CLICKED, {
      ...getBaseEventProps({
        feature: "account_email",
        accountId: activeAccountId || ""
      })
    })
    await refetchCheckEmailConnection()
    setEmailMessageToSend(emailTemplateRef.current!.getHTML())
    setEmailRecipient(emailTemplateModal)
    setConnectEmailProviderModalOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo, emailTemplateModal])

  const messageActions = useMemo(() => {
    const currentIndex = generatedEmailTemplates.findIndex(
      (template) => template.templateId === emailTemplateResponse?.templateId
    )

    return emailTemplateResponse ? (
      <div className="flex items-center justify-end gap-2">
        {generatedEmailTemplates.length > 1 ? (
          <>
            <div className="flex gap-2 items-center">
              <CustomTooltip title={t("label_previous_message")}>
                <Button
                  icon={<Undo2 size={16} />}
                  className="h-7 text-xs rounded-lg"
                  onClick={() => {
                    track(EVENTS.EMAIL.EMAIL_MESSAGE_NAVIGATION_BACK_CLICKED, {
                      ...getBaseEventProps({
                        feature: "account_email",
                        accountId: activeAccountId || ""
                      }),
                      navigation_number: currentIndex - 1
                    })
                    setEmailTemplateResponse(
                      generatedEmailTemplates[currentIndex - 1]
                    )
                  }}
                  disabled={currentIndex <= 0}
                />
              </CustomTooltip>
              <p className="text-13 text-dark-2 dark:text-dark-6">
                {currentIndex + 1}/{generatedEmailTemplates.length}
              </p>
              <CustomTooltip title={t("label_next_message")}>
                <Button
                  icon={<Redo2 size={16} />}
                  className="h-7 text-xs rounded-lg"
                  onClick={() => {
                    track(
                      EVENTS.EMAIL.EMAIL_MESSAGE_NAVIGATION_FORWARD_CLICKED,
                      {
                        ...getBaseEventProps({
                          feature: "account_email",
                          accountId: activeAccountId || ""
                        }),
                        navigation_number: currentIndex + 1
                      }
                    )
                    setEmailTemplateResponse(
                      generatedEmailTemplates[currentIndex + 1]
                    )
                  }}
                  disabled={currentIndex >= generatedEmailTemplates.length - 1}
                />
              </CustomTooltip>
            </div>
            <Divider type="vertical" className="h-6 bg-gray-200" />
          </>
        ) : null}
        <Button
          className={`w-fit h-7 text-xs rounded-lg dark:bg-dark-2 text-dark-2 font-medium dark:text-dark-6 ${
            copiedText
              ? "!bg-green-500 focus:!bg-green-500 hover:!bg-green-500 !text-white hover:text-white focus:text-white"
              : ""
          }`}
          onClick={async () => {
            track(EVENTS.EMAIL.COPY_MESSAGE_CLICKED, {
              ...getBaseEventProps({
                feature: "account_email",
                accountId: activeAccountId || ""
              })
            })
            if (emailTemplateRef.current) {
              const clone = emailTemplateRef.current.cloneNode(
                true
              ) as HTMLElement
              const firstEl = clone.firstElementChild
              if (firstEl?.textContent?.trimStart().startsWith("Subject:")) {
                clone.removeChild(firstEl)
              }
              await copy(clone.innerHTML)
            }
          }}
          disabled={!emailTemplateResponse}
          icon={copiedText ? <CopyCheck size={14} /> : <Copy size={14} />}
        />
        {emailTemplateModal?.templateType === "LINKEDIN" ? (
          <ButtonPrimary
            className={`h-7 text-xs rounded-lg text-primary dark:text-dark-6 ${
              linkedinCopiedText
                ? "!bg-green-500 focus:!bg-green-500 hover:!bg-green-500 !text-white hover:text-white focus:text-white"
                : ""
            }`}
            onClick={async () => {
              track(EVENTS.EMAIL.LINKEDIN_MESSAGE_CLICKED, {
                ...getBaseEventProps({
                  feature: "account_email",
                  accountId: activeAccountId || ""
                })
              })
              await linkedinCopy(emailTemplateRef.current!.textContent!)
              await sleep(300).promise
              window.open(
                `https://www.linkedin.com/sales/lead/${emailTemplateResponse?.source?.linkedin_urn},NAME_SEARCH,-jW6`,
                "_blank",
                "rel=noopener noreferrer"
              )
            }}
            icon={<LinkedinIcon size={14} />}
          >
            {t("label_send_via_linkedin")} <ArrowUpRight className="w-4 h-4" />
          </ButtonPrimary>
        ) : (
          <ButtonPrimary
            className="h-7 text-xs rounded-lg"
            icon={<Mail size={16} />}
            onClick={onClickNewEmail}
            loading={isRefetchingEmailConnection}
          >
            {t("label_send_email")}
            <ArrowUpRight className="w-4 h-4" />
          </ButtonPrimary>
        )}
      </div>
    ) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generatedEmailTemplates,
    emailTemplateModal?.templateType,
    emailTemplateResponse,
    copiedText,
    linkedinCopiedText,
    onClickNewEmail,
    isRefetchingEmailConnection,
    copy,
    linkedinCopy
  ])

  const isLinkedinTemplate = emailTemplateModal?.templateType === "LINKEDIN"

  return (
    <Drawer
      title={
        <p className="text-base font-semibold text-dark-2 dark:text-dark-6">
          {isLinkedinTemplate ? (
            <Trans
              i18nKey="account.email_template_drawer_title_linkedin"
              values={{
                name:
                  getLocationDisplay(emailTemplateModal?.name) ||
                  emailTemplateModal?.name ||
                  ""
              }}
              components={[
                <span
                  className="text-primary dark:text-primary-light"
                  key="n"
                />
              ]}
            />
          ) : (
            <Trans
              i18nKey="account.email_template_drawer_title_email"
              values={{
                name:
                  getLocationDisplay(emailTemplateModal?.name) ||
                  emailTemplateModal?.name ||
                  ""
              }}
              components={[
                <span
                  className="text-primary dark:text-primary-light"
                  key="n"
                />
              ]}
            />
          )}
        </p>
      }
      maskClosable={false}
      open={!!emailTemplateModal}
      destroyOnHidden={true}
      width={"55%"}
      footer={null}
      onClose={onClose}
      closeIcon={
        <span
          data-testid={
            isLinkedinTemplate
              ? "close-linkedin-message"
              : "close-email-message"
          }
        >
          <CloseOutlined className="dark:text-dark-6 text-dark-2" />
        </span>
      }
      classNames={{
        header: "dark:!border-border",
        body: "flex flex-col gap-3 !p-0",
        footer: "!px-6 !border-t-0"
      }}
    >
      <div className="h-full flex flex-col gap-3">
        <div className="flex flex-col flex-1 h-full">
          <EmailBody
            emailTemplateRef={emailTemplateRef}
            activeAccountId={activeAccountId!}
            activeOpportunityId={activeOpportunityId!}
            emailTemplateResponse={emailTemplateResponse}
            setEmailTemplateResponse={setEmailTemplateResponse}
            prevStreamResponse={prevStreamResponse}
            reader={reader}
            messageActions={messageActions}
            setGeneratedEmailTemplates={setGeneratedEmailTemplates}
            pollCancelledRef={pollCancelledRef}
          />
        </div>
      </div>
    </Drawer>
  )
}

const EmailBody = memo(
  ({
    emailTemplateRef,
    activeAccountId,
    activeOpportunityId,
    emailTemplateResponse,
    setEmailTemplateResponse,
    prevStreamResponse,
    reader,
    messageActions,
    setGeneratedEmailTemplates,
    pollCancelledRef
  }: {
    emailTemplateRef: any
    activeAccountId: string
    activeOpportunityId: string
    emailTemplateResponse: any
    setEmailTemplateResponse: Dispatch<SetStateAction<any>>
    prevStreamResponse: MutableRefObject<any>
    reader: MutableRefObject<ReadableStreamDefaultReader | null>
    messageActions: ReactNode
    setGeneratedEmailTemplates: Dispatch<SetStateAction<any[]>>
    pollCancelledRef: MutableRefObject<boolean>
  }) => {
    const { t } = useTranslation()
    const [inProgress, setInProgress] = useState(false)
    const [isRegenerating, setIsRegenerating] = useState(false)
    const emailTemplateModal = useAccountsStore(
      (store) => store.emailTemplateModal
    )
    const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
    const tenant = useGlobalStore((store) => store.tenant)
    const [emailTemplate, setEmailTemplate] = useState(
      () =>
        emailTemplateResponse?.new_text ??
        emailTemplateResponse?.content?.parts?.[0] ??
        emailTemplateResponse?.template ??
        ""
    )

    const { data: accountsData } = useGetAccounts()
    const activeAccount = accountsData?.data?.accounts?.find(
      (account: IAccount) => account.id === +(activeAccountId || "")
    )
    const [error, setError] = useState("")
    const tokenInfo = useGlobalStore((store) => store.tokenInfo)
    const [isLoading, setIsLoading] = useState(false)

    const pollPersona = useCallback(async () => {
      if (pollCancelledRef.current) return
      if (!emailTemplateModal) return
      const response = await getPersonDetails(
        tokenInfo!,
        emailTemplateModal.linkedin_url
      )
      const responseData = response.data.data
      if (responseData.persona_idetification_state === "IDENTIFIED") {
        track("fetch_persona", {
          name: responseData.name,
          id: responseData.id,
          state: "success"
        })
        return responseData
      } else if (
        responseData.persona_idetification_state === "INVALID_LINKEDIN_URL"
      ) {
        track("fetch_persona", {
          name: responseData.name,
          id: responseData.id,
          state: "failed"
        })
        return responseData
      }
      if (response.data.data.persona_idetification_state === "IDENTIFIED") {
        track(EVENTS.PERSONA.FETCH_PERSONA, {
          ...getBaseEventProps({
            feature: "account_email",
            accountId: activeAccountId || "",
            accountName:
              activeAccount?.short_name ?? activeAccount?.account_name
          }),
          name: responseData.name,
          id: responseData.id,
          state: "success"
        })
        return responseData
      } else if (
        responseData.persona_idetification_state === "INVALID_LINKEDIN_URL"
      ) {
        track(EVENTS.PERSONA.FETCH_PERSONA, {
          ...getBaseEventProps({
            feature: "account_email",
            accountId: activeAccountId || "",
            accountName:
              activeAccount?.short_name ?? activeAccount?.account_name
          }),
          name: responseData.name,
          id: responseData.id,
          state: "failed"
        })
        return responseData
      } else if (
        responseData.persona_idetification_state ===
        "IDENTIFICATION_IN_PROGRESS"
      ) {
        const { promise, clear } = sleep(3000)
        await promise
        clear()
        return pollPersona()
      }
    }, [
      pollCancelledRef,
      emailTemplateModal,
      tokenInfo,
      activeAccountId,
      activeAccount?.short_name,
      activeAccount?.account_name
    ])

    const getEmailTemplate = useCallback(
      async (userPrompt: string = "", retry = true) => {
        let shouldRetry = false
        if (reader.current) {
          try {
            await reader.current.cancel()
          } catch {
            // do nothing
          }
          reader.current = null
        }
        if (!emailTemplateModal) return
        setError("")
        if (!userPrompt) {
          setEmailTemplate("")
          setEmailTemplateResponse(null)
        }
        try {
          setInProgress(true)
          setIsLoading(true)
          let obj = {
            linkedin_url: emailTemplateModal.linkedin_url,
            account_id: activeAccountId,
            people_id: emailTemplateModal.id,
            linkedin_new_connection_flag:
              emailTemplateModal.templateType === "LINKEDIN",
            personApolloObj: emailTemplateModal
          } as Record<string, any>

          if (emailTemplateModal.opportunity_ids?.length) {
            obj.opportunity_ids = emailTemplateModal.opportunity_ids
          }
          if ((emailTemplateModal as any).is_crm_opp) {
            obj.is_crm_opp = true
          }

          if (userPrompt) {
            obj.user_prompt = userPrompt
            obj.previous_generated_email_id = emailTemplateResponse.templateId
          }

          let url = `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/msg-template/stream`
          if (emailTemplateModal.pageType === "KEY_EXECUTIVE_CHANGES") {
            url = `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/people_tracking/email-template`
            obj = {
              type: emailTemplateModal.roleType,
              name: emailTemplateModal.name,
              account_name: emailTemplateModal.account_name,
              id: emailTemplateModal.id
            }
          }
          if (emailTemplateModal.pageType === "EVENT_PEOPLE") {
            url = `${URLS.V1}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/people_tracking/email-template`
            obj = {
              type: "event-outreach",
              name: emailTemplateModal.name,
              account_name: emailTemplateModal.account_name,
              id: emailTemplateModal.id,
              linkedin_url: emailTemplateModal.linkedin_url,
              event_id: emailTemplateModal.event_id,
              event_name: emailTemplateModal.event_name
            }
          }
          if (linkedTenantId) {
            url += `?linked_tenant_id=${linkedTenantId}`
          }
          const isNonStreamingPage =
            emailTemplateModal.pageType === "KEY_EXECUTIVE_CHANGES" ||
            emailTemplateModal.pageType === "EVENT_PEOPLE"
          let isIdentified = false
          if (!isNonStreamingPage) {
            pollCancelledRef.current = false
            const pollPersonaResonse = await pollPersona()
            isIdentified =
              pollPersonaResonse.persona_idetification_state === "IDENTIFIED" ||
              pollPersonaResonse.persona_idetification_state ===
                "INVALID_LINKEDIN_URL"
          }
          if (isIdentified || isNonStreamingPage) {
            const response = await retryFetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${tokenInfo?.token}`
              },
              body: JSON.stringify(obj)
            })

            if (isNonStreamingPage) {
              const jsonResponse = await response.json()
              setInProgress(false)
              setEmailTemplate(jsonResponse.data.template)
              setEmailTemplateResponse(jsonResponse.data)
              return
            }

            if (response.status === 400) {
              throw new Error(t("account.error_generating_message"))
            }
            if (response.status === 500) {
              throw new Error("Internal server error")
            } else if (!response.body) {
              throw new Error("ReadableStream not supported in this browser.")
            } else {
              reader.current = response.body.getReader()
              const decoder = new TextDecoder("utf-8")
              let result = ""
              let debounceTimeout: NodeJS.Timeout | null = null
              const processEvent = async (event: string) => {
                setIsRegenerating(false)
                setIsLoading(false)
                const _event = event.trim()
                if (_event && _event.startsWith("data:")) {
                  try {
                    const data = JSON.parse(_event.replace("data: ", ""))
                    if (data.error) {
                      throw new Error(data.error)
                    } else {
                      const textMessageType = data.message.content.content_type
                      const message = data.message.content.parts[0]
                      if (textMessageType === "text" && message) {
                        if (debounceTimeout) clearTimeout(debounceTimeout)
                        debounceTimeout = setTimeout(() => {
                          setEmailTemplate(message)
                        }, 20)
                      }
                    }
                    const status = data.message.status

                    if (status === "COMPLETED") {
                      setEmailTemplateResponse(data.message)
                      setGeneratedEmailTemplates((prev) => [
                        ...prev,
                        data.message
                      ])
                      prevStreamResponse.current = data.message
                    } else if (status === "ERROR") {
                      if (retry) {
                        shouldRetry = true
                      } else {
                        setError(
                          "Failed to generate email template. Please try again."
                        )
                      }
                    }

                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch {
                    // do nothing
                  }
                } else if (_event) {
                  try {
                    const data = JSON.parse(_event)
                    if (data.error) {
                      throw new Error(data.error)
                    } else if (data.new_text) {
                      setEmailTemplate(data.new_text)
                      setEmailTemplateResponse(data)
                      setGeneratedEmailTemplates((prev) => [...prev, data])
                      prevStreamResponse.current = data
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    // do nothing
                  }
                }
              }
              // eslint-disable-next-line no-constant-condition
              while (true) {
                const { done, value } = await reader.current.read()

                if (done) {
                  result += decoder.decode(new Uint8Array(), { stream: false })
                  break
                }
                const decodedChunk = decoder.decode(value, { stream: true })
                result += decodedChunk
                // // Process the streamed data here
                const events = result
                  .split("\n")
                  .reduce((acc: string[], cur: string) => {
                    const val = cur.trim()
                    if (!val.startsWith("id:")) acc.push(val)
                    return acc
                  }, [])
                events.forEach(processEvent)
              }
              if (shouldRetry) {
                await getEmailTemplate(userPrompt, false)
              }
            }
          }
        } catch (error: any) {
          setError(error.message)
        } finally {
          setIsLoading(false)
          setInProgress(false)
          reader.current?.cancel()
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        tokenInfo,
        activeAccountId,
        activeOpportunityId,
        emailTemplateModal,
        emailTemplate,
        emailTemplateResponse,
        linkedTenantId
      ]
    )

    useEffect(() => {
      if (!emailTemplateResponse?.id && !prevStreamResponse.current) {
        getEmailTemplate()
      } else if (emailTemplateResponse?.new_text) {
        setEmailTemplate(emailTemplateResponse.new_text)
      } else if (emailTemplateResponse?.email_text) {
        setEmailTemplate(emailTemplateResponse.email_text)
      } else if (emailTemplateResponse?.content?.parts?.[0]) {
        setEmailTemplate(emailTemplateResponse.content.parts[0])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailTemplateResponse?.id])

    useEffect(() => {
      if (prevStreamResponse.current) {
        setEmailTemplateResponse(prevStreamResponse.current)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      return () => {
        setEmailTemplateResponse(null)
        setEmailTemplate("")
        reader.current?.cancel()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailTemplateModal?.id])

    const dropList = useMemo(() => {
      return emailTemplateResponse?.source?.source?.map(
        (source: any, index: number) => {
          const link = source.cdnLink || source.urlLink
          const name =
            getLocationDisplay(source.title) ||
            getLocationDisplay(source.name) ||
            source.title ||
            source.name
          return {
            key: index.toString(),
            label: !link ? (
              <p className="text-xs dark:text-dark-6 dark-text-2">{name}</p>
            ) : (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-primary text-xs"
              >
                {clipText(name, 10, 25)}
              </a>
            )
          }
        }
      )
    }, [emailTemplateResponse?.source?.source])

    const newsDropList = useMemo(() => {
      return emailTemplateResponse?.source?.newsSources?.map(
        (source: any, index: number) => {
          const link = source.news_source_link || source.link
          const name =
            getLocationDisplay(source.title) ||
            getLocationDisplay(source.name) ||
            source.title ||
            source.name
          return {
            key: index.toString(),
            label: !link ? (
              <p className="text-xs dark:text-dark-6 dark-text-2 w-80 line-clamp-1">
                {name}
              </p>
            ) : (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="underline text-primary text-xs w-80 line-clamp-1"
              >
                {clipText(name, 10, 35)}
              </a>
            )
          }
        }
      )
    }, [emailTemplateResponse?.source?.newsSources])

    const personNewsDropList = useMemo(() => {
      return emailTemplateResponse?.source?.personLevelNews?.map(
        (source: any, index: number) => {
          const link = source.link
          const name = getLocationDisplay(source.title) || source.title
          return {
            key: index.toString(),
            label: !link ? (
              <p className="text-xs dark:text-dark-6 dark-text-2">{name}</p>
            ) : (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-primary text-xs"
              >
                {clipText(name, 10, 25)}
              </a>
            )
          }
        }
      )
    }, [emailTemplateResponse?.source?.personLevelNews])

    const relevantCompanyBlogsDropList = useMemo(() => {
      return emailTemplateResponse?.source?.relevantCompanyBlogs?.map(
        (source: any, index: number) => {
          const link = source.link
          const name = getLocationDisplay(source.title) || source.title
          return {
            key: index.toString(),
            label: !link ? (
              <p className="text-xs dark:text-dark-6 dark-text-2">{name}</p>
            ) : (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-primary text-xs"
              >
                {clipText(name, 10, 25)}
              </a>
            )
          }
        }
      )
    }, [emailTemplateResponse?.source?.relevantCompanyBlogs])

    const availableSources = useMemo(() => {
      const output: string[] = []
      if (emailTemplateResponse?.source?.oppContexts?.length)
        output.push(t("account.email_template_source_opportunity_context"))
      if (emailTemplateResponse?.source?.source?.length)
        output.push(
          t("account.email_template_source_tenant_knowledge", {
            tenantName: tenant?.name ?? ""
          })
        )
      if (emailTemplateResponse?.source?.relevantCompanyBlogs?.length > 0)
        output.push(t("account.email_template_source_related_company_blogs"))
      if (emailTemplateResponse?.source?.personLevelNews?.length > 0)
        output.push(t("account.email_template_source_related_person_news"))
      if (emailTemplateResponse?.source?.financialSources?.length > 0)
        output.push(t("account.email_template_source_financial"))
      if (emailTemplateResponse?.source?.newsSources?.length > 0)
        output.push(t("account.email_template_source_related_news"))
      if (emailTemplateResponse?.source?.linkedinUrl)
        output.push(t("account.email_template_source_linkedin_profile"))
      return output
    }, [emailTemplateResponse, tenant?.name, t])

    const onFinish = useCallback(
      async (userPrompt: string) => {
        track(EVENTS.EMAIL.EMAIL_PROMPT_SUBMITTED, {
          ...getBaseEventProps({
            feature: "account_email",
            accountId: activeAccountId || "",
            accountName:
              activeAccount?.short_name ?? activeAccount?.account_name
          }),
          text: userPrompt,
          trigger: "Enter"
        })
        setIsRegenerating(true)
        getEmailTemplate(userPrompt)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getEmailTemplate]
    )

    return (
      <>
        {isLoading && !isRegenerating ? (
          <div className="p-4">
            <EmailTemplateLoader
              tenantName={tenant?.name ?? ""}
              accountName={activeAccount?.short_name ?? ""}
              activeOpportunity={emailTemplateResponse?.opportunity_name ?? ""}
              className="border border-light-border dark:border-border rounded-lg"
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-3 overflow-hidden p-6">
            {error ? (
              <p className="text-13 text-red-500 dark:text-red-400 text-center font-medium">
                {t("account.error_generating_message")}
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-4 relative overflow-auto">
                  {isRegenerating && (
                    <div className="absolute z-50 bg-transparent left-0 right-0 top-0 bottom-0 flex items-center justify-center">
                      <div className="absolute left-0 right-0 top-0 bottom-0 bg-white dark:bg-dark-1 opacity-60 z-0" />
                      <div className="flex gap-2 items-center justify-center max-h-96 overflow-auto bg-white dark:bg-dark-1 rounded-lg p-6 opacity-100 z-50 border border-primary dark:border-primary-light">
                        <OSLoader className="xs" />
                        <p className="text-xs text-dark-2 dark:text-dark-6 text-center">
                          Regenerating the message, this might take a few
                          seconds.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="bg-[whitesmoke] dark:bg-dark-2 border-[0.5px] border-light-border dark:border-border rounded-md p-3 max-h-[calc(100vh-200px)] overflow-auto">
                    <MarkdownRender
                      editorInstanceRef={emailTemplateRef}
                      content={emailTemplate}
                      className="!p-0"
                    />
                  </div>
                  {inProgress && !isRegenerating ? null : (
                    <div className="flex justify-between items-start">
                      {availableSources.length > 0 ? (
                        <div className="flex gap-2">
                          <p className="text-xs font-semibold text-dark-2 dark:text-dark-6">
                            {t("label_sources")}:
                          </p>
                          <div className={"flex gap-2 items-center flex-wrap"}>
                            {emailTemplateResponse?.source?.oppContexts?.length
                              ? emailTemplateResponse?.source?.oppContexts.map(
                                  (context: any) => (
                                    <Tooltip
                                      key={
                                        getLocationDisplay(context.name) ||
                                        context.name
                                      }
                                      title={
                                        <MarkdownRender
                                          content={context.rationale_string_2}
                                        />
                                      }
                                      arrow={false}
                                      classNames={{
                                        body: "!bg-genesis dark:!bg-dark-1 w-96"
                                      }}
                                    >
                                      <Tag
                                        color="purple"
                                        className="cursor-pointer text-xs leading-5 mr-0 !bg-[#CBF1EA] !border-[#00B894] !text-dark-2 rounded-lg"
                                        onClick={() => {
                                          track(EVENTS.COMMON.SOURCE_CLICKED, {
                                            ...getBaseEventProps({
                                              feature: "account_email",
                                              accountId: activeAccountId,
                                              accountName:
                                                activeAccount?.short_name ??
                                                activeAccount?.account_name
                                            }),
                                            source_name: "Opportunity context",
                                            sources_available: availableSources,
                                            triggered_from:
                                              "email personalisation"
                                          })
                                        }}
                                      >
                                        {getLocationDisplay(context.name) ||
                                          context.name}
                                      </Tag>
                                    </Tooltip>
                                  )
                                )
                              : null}
                            {emailTemplateResponse?.source?.linkedinUrl ? (
                              <a
                                href={sanitizeUrl(
                                  emailTemplateResponse?.source?.linkedinUrl
                                )}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline"
                                onClick={() => {
                                  track(EVENTS.COMMON.SOURCE_CLICKED, {
                                    ...getBaseEventProps({
                                      feature: "account_email",
                                      accountId: activeAccountId,
                                      accountName:
                                        activeAccount?.short_name ??
                                        activeAccount?.account_name
                                    }),
                                    source_name: t(
                                      "account.email_template_source_linkedin_profile"
                                    ),
                                    sources_available: availableSources,
                                    triggered_from: "email personalisation"
                                  })
                                }}
                              >
                                <Tag className="cursor-pointer text-xs leading-5 mr-0 !bg-primary !text-white !border-primary rounded-lg">
                                  {t(
                                    "account.email_template_source_linkedin_profile"
                                  )}
                                </Tag>
                              </a>
                            ) : null}
                            {emailTemplateResponse?.source?.source?.length >
                            0 ? (
                              <Dropdown
                                arrow={false}
                                menu={{
                                  items: dropList
                                }}
                                placement="bottomLeft"
                              >
                                <Tag
                                  color="green"
                                  className="rounded-lg text-xs leading-5 cursor-pointer mr-0"
                                  onClick={() => {
                                    track(EVENTS.COMMON.SOURCE_CLICKED, {
                                      ...getBaseEventProps({
                                        feature: "account_email",
                                        accountId: activeAccountId,
                                        accountName:
                                          activeAccount?.short_name ??
                                          activeAccount?.account_name
                                      }),
                                      source_name: `${tenant?.name} knowledge sources`,
                                      sources_available: availableSources,
                                      triggered_from: "email personalisation"
                                    })
                                  }}
                                >
                                  {`${tenant?.name} knowledge sources`}
                                </Tag>
                              </Dropdown>
                            ) : null}
                            {emailTemplateResponse?.source?.financialSources
                              ?.length > 0 ? (
                              <Tooltip
                                title={
                                  <MarkdownRender
                                    content={
                                      emailTemplateResponse?.source
                                        ?.financialSources[0].beautified_body
                                    }
                                  />
                                }
                                arrow={false}
                                classNames={{
                                  body: "!bg-genesis dark:!bg-dark-1 w-96 h-96 overflow-auto"
                                }}
                              >
                                <Tag
                                  color="orangered"
                                  className="cursor-pointer text-xs leading-5 mr-0 !bg-[#CBF1EA] !border-[#00B894] !text-dark-2 rounded-lg"
                                  onClick={() => {
                                    track(EVENTS.COMMON.SOURCE_CLICKED, {
                                      ...getBaseEventProps({
                                        feature: "account_email",
                                        accountId: activeAccountId,
                                        accountName:
                                          activeAccount?.short_name ??
                                          activeAccount?.account_name
                                      }),
                                      source_name: "Financial sources",
                                      sources_available: availableSources,
                                      triggered_from: "email personalisation"
                                    })
                                  }}
                                >
                                  Financial sources
                                </Tag>
                              </Tooltip>
                            ) : null}
                            {emailTemplateResponse?.source?.newsSources
                              ?.length > 0 ? (
                              <Dropdown
                                arrow={false}
                                menu={{ items: newsDropList }}
                                placement="bottomLeft"
                                rootClassName="w-80 min-w-80"
                              >
                                <Tag
                                  color="green"
                                  className="rounded-lg text-xs leading-5 cursor-pointer"
                                  onClick={() => {
                                    track(EVENTS.COMMON.SOURCE_CLICKED, {
                                      ...getBaseEventProps({
                                        feature: "account_email",
                                        accountId: activeAccountId,
                                        accountName:
                                          activeAccount?.short_name ??
                                          activeAccount?.account_name
                                      }),
                                      source_name: t(
                                        "account.email_template_source_related_news"
                                      ),
                                      sources_available: availableSources,
                                      triggered_from: "email personalisation"
                                    })
                                  }}
                                >
                                  {t(
                                    "account.email_template_source_related_news"
                                  )}
                                </Tag>
                              </Dropdown>
                            ) : null}
                            {emailTemplateResponse?.source?.relevantCompanyBlogs
                              ?.length > 0 ? (
                              <Dropdown
                                arrow
                                menu={{ items: relevantCompanyBlogsDropList }}
                                placement="bottomLeft"
                              >
                                <Tag
                                  color="green"
                                  className="rounded-lg cursor-pointer"
                                  onClick={() => {
                                    track(EVENTS.COMMON.SOURCE_CLICKED, {
                                      ...getBaseEventProps({
                                        feature: "account_email",
                                        accountId: activeAccountId,
                                        accountName:
                                          activeAccount?.short_name ??
                                          activeAccount?.account_name
                                      }),
                                      source_name: "Related Company Blogs",
                                      sources_available: availableSources,
                                      triggered_from: "email personalisation"
                                    })
                                  }}
                                >
                                  Related Company Blogs
                                </Tag>
                              </Dropdown>
                            ) : null}
                            {emailTemplateResponse?.source?.personLevelNews
                              ?.length > 0 ? (
                              <Dropdown
                                arrow
                                menu={{ items: personNewsDropList }}
                                placement="bottomLeft"
                              >
                                <Tag
                                  color="green"
                                  className="rounded-lg cursor-pointer"
                                  onClick={() => {
                                    track(EVENTS.COMMON.SOURCE_CLICKED, {
                                      ...getBaseEventProps({
                                        feature: "account_email",
                                        accountId: activeAccountId,
                                        accountName:
                                          activeAccount?.short_name ??
                                          activeAccount?.account_name
                                      }),
                                      source_name: "Related Person News",
                                      sources_available: availableSources,
                                      triggered_from: "email personalisation"
                                    })
                                  }}
                                >
                                  Related Person News
                                </Tag>
                              </Dropdown>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div />
                      )}
                      {messageActions}
                    </div>
                  )}
                </div>
                {(inProgress && !isRegenerating && !isLoading) ||
                emailTemplateModal?.pageType ===
                  "KEY_EXECUTIVE_CHANGES" ? null : (
                  <MessageForm
                    isRegenerating={isRegenerating}
                    onFinish={onFinish}
                  />
                )}
              </>
            )}
          </div>
        )}
      </>
    )
  }
)
EmailBody.displayName = "EmailBody"

export const EmailTemplateLoader = ({
  tenantName,
  accountName,
  activeOpportunity,
  className
}: {
  tenantName: string
  accountName?: string
  activeOpportunity?: string
  className?: string
}) => {
  const tenantType = useGlobalStore((store) => store.tenant?.tenant_type)
  const isOSLite = tenantType?.includes("os_lite")
  const emailTemplateModal = useAccountsStore(
    (store) => store.emailTemplateModal
  )
  const showMessageTemplateModal = useChampionsTrackingStore(
    (store) => store.showMessageTemplateModal
  )
  const template = emailTemplateModal || showMessageTemplateModal
  const templateName = getLocationDisplay(template?.name) || template?.name
  const stepsValues = useMemo(() => {
    const strong = <strong className="text-primary dark:text-primary-light" />
    const bold = <b className="text-primary dark:text-primary-light" />
    return (
      [
        {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {templateName ? (
                <Trans
                  i18nKey="account.email_template_loader_search_web_named"
                  values={{ name: templateName }}
                  components={[strong]}
                />
              ) : (
                <Trans
                  i18nKey="account.email_template_loader_search_web_anon"
                  components={[strong]}
                />
              )}
            </p>
          ),
          children: [
            {
              title: (
                <p className="text-13 dark:text-dark-6 text-dark-2">
                  {templateName ? (
                    <Trans
                      i18nKey="account.email_template_loader_news_about_named"
                      values={{ name: templateName }}
                      components={[strong]}
                    />
                  ) : (
                    <Trans
                      i18nKey="account.email_template_loader_news_about_anon"
                      components={[strong]}
                    />
                  )}
                </p>
              )
            },
            {
              title: (
                <p className="text-13 dark:text-dark-6 text-dark-2">
                  {templateName ? (
                    <Trans
                      i18nKey="account.email_template_loader_social_named"
                      values={{ name: templateName }}
                      components={[strong]}
                    />
                  ) : (
                    <Trans
                      i18nKey="account.email_template_loader_social_anon"
                      components={[strong]}
                    />
                  )}
                </p>
              )
            }
          ]
        },
        accountName && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              <Trans
                i18nKey="account.email_template_loader_account_blogs"
                values={{ accountName }}
                components={[strong]}
              />
            </p>
          )
        },
        !isOSLite && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              <Trans
                i18nKey="account.email_template_loader_tenant_knowledge"
                values={{ tenantName }}
                components={[strong]}
              />
            </p>
          )
        },
        activeOpportunity && {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              <Trans
                i18nKey="account.email_template_loader_opportunity_signals"
                values={{ opportunity: activeOpportunity }}
                components={[bold]}
              />
            </p>
          )
        },
        {
          title: (
            <p className="text-13 dark:text-dark-6 text-dark-2">
              {template?.templateType === "LINKEDIN" ? (
                templateName ? (
                  <Trans
                    i18nKey="account.email_template_loader_personalize_linkedin_named"
                    values={{ name: templateName }}
                    components={[bold]}
                  />
                ) : (
                  <Trans
                    i18nKey="account.email_template_loader_personalize_linkedin_anon"
                    components={[bold]}
                  />
                )
              ) : templateName ? (
                <Trans
                  i18nKey="account.email_template_loader_personalize_email_named"
                  values={{ name: templateName }}
                  components={[bold]}
                />
              ) : (
                <Trans
                  i18nKey="account.email_template_loader_personalize_email_anon"
                  components={[bold]}
                />
              )}
            </p>
          )
        }
      ] as (
        | {
            title: ReactNode
            children?: { title: ReactNode }[]
          }
        | false
        | undefined
      )[]
    ).filter(Boolean) as {
      title: ReactNode
      children?: { title: ReactNode }[]
    }[]
  }, [
    template?.templateType,
    templateName,
    tenantName,
    activeOpportunity,
    accountName,
    isOSLite
  ])
  const [steps, setSteps] = useState<
    { title: ReactNode; children?: { title: ReactNode }[] }[]
  >([
    {
      title: (
        <p className="text-13 dark:text-dark-6 text-dark-2">
          {templateName ? (
            <Trans
              i18nKey="account.email_template_loader_search_web_named"
              values={{ name: templateName }}
              components={[
                <strong
                  className="text-primary dark:text-primary-light"
                  key="h"
                />
              ]}
            />
          ) : (
            <Trans
              i18nKey="account.email_template_loader_search_web_anon"
              components={[
                <strong
                  className="text-primary dark:text-primary-light"
                  key="h"
                />
              ]}
            />
          )}
        </p>
      )
    }
  ])
  const [currentStep, setCurrentStep] = useState({ parent: 0, child: 0 })

  const initializeSteps = useCallback(async () => {
    for (let i = 0; i < stepsValues.length; i++) {
      const { promise, clear } = sleep(600 * i)
      await promise
      clear()
      const _previousSteps = stepsValues.slice(0, i)
      const currentStep = {
        ...stepsValues[i],
        children: [] as any[]
      }
      const children = stepsValues[i].children
      if (children?.length) {
        for (let j = 0; j < children.length; j++) {
          const { promise, clear } = sleep(1200 * (j + i + 1))
          await promise
          clear()
          currentStep.children = [...currentStep.children, children[j]]
          setSteps([..._previousSteps, currentStep])
          setCurrentStep((prev) => ({ ...prev, child: j + 1 }))
        }
      } else {
        setSteps([..._previousSteps, currentStep])
        setCurrentStep({ parent: i + 1, child: 0 })
      }
      setCurrentStep({ parent: i + 1, child: 0 })
    }
  }, [stepsValues])

  useEffect(() => {
    initializeSteps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ul className={`flex flex-col gap-4 p-4 ${className}`}>
        {steps.map((step, index) => (
          <li key={index} className="text-xs flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              {index + 1 < currentStep.parent ? (
                <div className="w-5 h-5 rounded-full flex items-center justify-center bg-primary">
                  <CheckCheck size={16} color="#fff" />
                </div>
              ) : (
                <OSLoader className="xs" />
              )}
              <span>{step.title}</span>
            </div>
            {step.children && (
              <Steps
                className="pl-8"
                direction="vertical"
                current={step.children.length}
                size="small"
                progressDot
                items={step.children.map((child) => ({
                  className: "h-8",
                  title: (
                    <div className="flex gap-2 items-center">
                      {child.title}
                      {index + 1 < currentStep.parent ? (
                        <CheckCircle size={16} color="#10b981" />
                      ) : (
                        <OSLoader className="xs" />
                      )}
                    </div>
                  )
                }))}
              />
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

const MessageForm = ({
  isRegenerating,
  onFinish
}: {
  isRegenerating: boolean
  onFinish: (userPrompt: string) => void
}) => {
  const { t } = useTranslation()
  const messageRef = useRef<HTMLTextAreaElement>(null)
  const [value, setValue] = useState("")

  const handleFocus = useCallback(() => {
    if (messageRef.current && document.activeElement !== messageRef.current) {
      messageRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (!isRegenerating) {
      setValue("")
    }
  }, [isRegenerating])

  return (
    <div className="flex flex-col gap-1">
      <label
        className="text-xs font-medium text-dark-2 dark:text-dark-6"
        htmlFor="input_message"
      >
        {t("account.label_refine_with_ai")}
      </label>
      <div
        className={`rounded-xl p-2 border-[0.5px] border-light-border dark:border-border overflow-hidden bg-genesis dark:bg-dark-2 ${isRegenerating ? "cursor-progress" : ""}`}
        onClick={handleFocus}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
      >
        <textarea
          placeholder={t("account.placeholder_improve_message")}
          name="input_message"
          id="input_message"
          className="!resize-none w-full outline-none border-none text-13 bg-transparent"
          value={value}
          onChange={(e) => setValue(e.target.value.trimStart())}
          disabled={isRegenerating}
          ref={messageRef}
        />
        <div className="flex justify-end">
          <button
            className="bg-primary text-white px-4 py-2 rounded-full"
            disabled={!value || isRegenerating}
            onClick={() => onFinish(value)}
          >
            {isRegenerating ? (
              <Loader style={{ fontSize: 12, color: "#fff" }} />
            ) : (
              <SendHorizonal size={16} color="#fff" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
